import React, { useCallback, useContext, useEffect, useState } from 'react';

import { StatsigClient } from '@statsig/js-client';
import { StatsigProvider } from '@statsig/react-bindings';

import { STATSIG_CLIENT_ID, STATSIG_TIER_NAME } from '../../constants/statsig';
import { buildStatsigUser } from '../../utils/statsig/statsigUser';
import { SessionContext } from '../auth/authContext';
import { CompanyContext } from '../company/companyContext';
import { PlansContext } from '../plans/plansContext';

const StatsigCustomProvider = ({ children }: React.PropsWithChildren) => {
  const [statsigClient, setStatsigClient] = useState<StatsigClient | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const { company, companyData, holdingList } = useContext(CompanyContext);
  const { email, userId } = useContext(SessionContext);
  const { plans, planType } = useContext(PlansContext);

  const createUserAttributes = useCallback(async () => {
    if (!statsigClient) return;
    if (userId) {
      await statsigClient.updateUserAsync(
        buildStatsigUser({ userId, email, holdingList, company, companyData, plans, planType })
      );
    } else {
      await statsigClient.updateUserAsync({});
    }
  }, [userId, holdingList, email, company, companyData, statsigClient, plans, planType]);

  useEffect(() => {
    if (statsigClient && userId && companyData.id) {
      createUserAttributes();
    }
  }, [userId, companyData, statsigClient]);

  useEffect(() => {
    const initializeStatsig = async () => {
      const client = new StatsigClient(
        STATSIG_CLIENT_ID,
        {},
        {
          environment: { tier: STATSIG_TIER_NAME }
        }
      );
      await client.initializeAsync();
      setStatsigClient(client);
      setIsInitializing(false);
    };

    if (!statsigClient) {
      initializeStatsig();
    }
  }, []);

  if (isInitializing || !statsigClient) {
    return <>{children}</>;
  }

  return <StatsigProvider client={statsigClient}>{children}</StatsigProvider>;
};

export default StatsigCustomProvider;
